import { useState } from "react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/sharp-regular-svg-icons";
import { Loader } from "@mantine/core";
import { FileRejection } from "@mantine/dropzone";
import { documentTypeDisplayValueMap } from "../../types/document";
import { DocumentUploadModalMode } from "../../types/modal";

import styles from "./upload-document-modal-view.module.scss";
import { Button } from "@/components/buttons/Button";
import { Toast } from "design-system/src/components/Toast";
import { AppealSupportingDocumentType } from "@/__generated__/graphql.ts";
import UploadFileInput from "@/components/forms/UploadFileInput";

const getDocumentUploadDescription = (
  documentType: AppealSupportingDocumentType,
) => {
  if (documentType === AppealSupportingDocumentType.Other) {
    return "You only need to upload this document if it was suggested by your provider or is required by the denial letter.";
  }

  const documentDisplayValue = documentTypeDisplayValueMap[documentType];
  const doesDocumentTypeStartWithVowel = ["a", "e", "i", "o", "u"].includes(
    documentDisplayValue.charAt(0).toLowerCase(),
  );

  return `You only need to upload ${doesDocumentTypeStartWithVowel ? "an" : "a"} ${documentDisplayValue.toLowerCase()} if it was suggested by your provider or is required by the denial letter.`;
};

const getLoadingText = (
  mode: DocumentUploadModalMode,
  isSubmitLoading: boolean,
) => {
  if (!isSubmitLoading) {
    return "LOading Document Files";
  }

  return mode === DocumentUploadModalMode.EDIT
    ? "Updating Document"
    : "Adding Document";
};

type UploadDocumentModalViewProps = {
  selectedDocumentType: AppealSupportingDocumentType;
  files: File[];
  onBack: () => void;
  onFilesUploaded: (files: File[]) => void;
  onViewFile: (file: File) => void;
  onSaveDocument: () => void;
  onDeleteFile: (file: File) => void;
  error?: string;
  isSubmitLoading: boolean;
  isLoadingInitialState: boolean;
  mode: DocumentUploadModalMode;
};

export const UploadDocumentModalView = ({
  selectedDocumentType,
  files,
  onBack,
  onFilesUploaded,
  onViewFile,
  onSaveDocument,
  onDeleteFile,
  error,
  isSubmitLoading,
  isLoadingInitialState,
  mode,
}: UploadDocumentModalViewProps) => {
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleOnFileRejected = (fileRejections: FileRejection[]) => {
    const errorCodes = fileRejections.flatMap((fileRejection) =>
      fileRejection.errors.map((fileError) => fileError.code),
    );

    if (errorCodes.length) {
      const [errorCode] = errorCodes;

      if (errorCode === "file-too-large") {
        setValidationError("Your file is too big. Files must be under 20mb.");
      } else {
        setValidationError(
          "File type not supported. Upload a JPG, PNG, or PDF.",
        );
      }
    } else {
      setValidationError(null);
    }
  };

  const handleViewFile = (file: File) => {
    setValidationError(null);
    onViewFile(file);
  };

  const handleSaveDocument = () => {
    setValidationError(null);
    onSaveDocument();
  };

  const handleFilesUploaded = (uploadedFiles: File[]) => {
    setValidationError(null);
    onFilesUploaded(uploadedFiles);
  };

  const handleFileDeleted = (fileToDelete: File) => {
    setValidationError(null);
    onDeleteFile(fileToDelete);
  };

  return (
    <>
      <h2 className={styles.modalContentHeader}>
        {`Upload your ${selectedDocumentType === AppealSupportingDocumentType.Other ? "document" : documentTypeDisplayValueMap[selectedDocumentType]}`}
      </h2>
      <p className={styles.modalContentDescription}>
        {getDocumentUploadDescription(selectedDocumentType)}
      </p>
      <div className={styles.errorMessage}>
        <Toast variant="error" isVisible={!!(error || validationError)}>
          {error ?? validationError}
        </Toast>
      </div>
      <div className={styles.fileUploadContainer}>
        <UploadFileInput
          className={styles.fileUpload}
          onViewFile={handleViewFile}
          files={files}
          disabled={isSubmitLoading}
          onFilesUploaded={handleFilesUploaded}
          onFileDeleted={handleFileDeleted}
          onFileRejected={handleOnFileRejected}
        />
        {(isSubmitLoading || isLoadingInitialState) && (
          <div className={styles.loadingOverlay}>
            <Loader className={styles.loadingSpinner} size={24} />
            <span className={styles.loadingText}>
              {getLoadingText(mode, isSubmitLoading)}
            </span>
          </div>
        )}
      </div>
      <div className={styles.uploadDocumentButtons}>
        <Button
          onClick={onBack}
          type="button"
          variant="tertiary"
          size="medium"
          disabled={isSubmitLoading || isLoadingInitialState}
        >
          <FontAwesomeIcon icon={faArrowLeft as IconDefinition} />
          {"Back"}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="medium"
          isFullWidth
          onClick={handleSaveDocument}
          disabled={!files.length || isSubmitLoading || isLoadingInitialState}
        >
          {mode === DocumentUploadModalMode.EDIT
            ? "Update Document"
            : "Add Document"}
        </Button>
      </div>
    </>
  );
};
